import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { HeaderGestor } from "components/HeaderGestor";
import { getGlobalLogo } from "services/settings.service";
import { updateGlobalLogo } from "services/settings.service";
import { useListPlayer } from 'hooks/player/useListPlayer';
import { useAuth } from "context/AuthContext";
import {
  Upload,
  BarChart3,
  Users,
  Settings,
  FileUp,
  CheckCircle2,
  Download,
  Edit,
  Trash2,
  UserPlus,
  Palette,
  Sparkles,
  Calendar,
  Clock,
  CloudUpload,
  AlertCircle,
  FileText
} from 'lucide-react';
import { Button } from 'flowbite-react';
import { getAllRankings, deleteRanking } from 'services/ranking.service';
import { importRankingFromGoogleSheets } from 'services/google-sheets.service';
import { useDataImport } from "hooks/registersData/useDataImport";
import { Ranking } from 'types/ranking';
import { ImportDataModal } from './ImportDataModal';
import { ImportParticipantsModal } from './ImportParticipantsModal';
import './styles.scss';
import { useWatch } from "react-hook-form";

interface ImportResult {
  total: number;
  success: number;
  errors: number;
  data: any[];
  errorDetails?: string[];
}

type TabId = "importar" | "rankings" | "participantes" | "configuracoes";

interface ImportHistory {
  date: string;
  total: number;
  success: number;
  errors: number;
  status: "success" | "warning" | "error";
}

const Gestao: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabId>("participantes");

  const tabs = [
    { id: "participantes" as TabId, label: "Participantes", icon: Users },
    { id: "rankings" as TabId, label: "Rankings", icon: BarChart3 },
    { id: "importar" as TabId, label: "Importar dados", icon: CloudUpload },
    { id: "configuracoes" as TabId, label: "Configurações", icon: Settings },
  ];

  // Estados para rankings
  const [rankingsList, setRankingsList] = useState<Ranking[]>([]);
  const [loadingRankings, setLoadingRankings] = useState(false);

  // Estados para logo
  const [logoUrl, setLogoUrl] = useState<string>("");
  const [logoData, setLogoData] = useState<any>(null);
  const [loadingLogo, setLoadingLogo] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Estados para importação
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isImportParticipantsModalOpen, setIsImportParticipantsModalOpen] = useState(false);
  const [isManualAddModalOpen, setIsManualAddModalOpen] = useState(false);
  const [googleSheetsUrl, setGoogleSheetsUrl] = useState('');

  const [importingFromSheets, setImportingFromSheets] = useState(false);
  const [importHistory, setImportHistory] = useState<ImportHistory[]>([
    {
      date: "15/01/2025 às 14:30",
      total: 156,
      success: 152,
      errors: 4,
      status: "success",
    },
    {
      date: "12/01/2025 às 09:15",
      total: 98,
      success: 94,
      errors: 4,
      status: "warning",
    },
    {
      date: "10/01/2025 às 16:45",
      total: 148,
      success: 148,
      errors: 0,
      status: "success",
    },
  ]);

  const { data: importData } = useDataImport();

  const dataTotal = importData.length;
  const totalCompleted = importData.filter(
    (item: any) => item.status === 'completed'
  ).length;

  const dataMaisRecenteFormatada = useMemo(() => {
    if (!importData.length) return "--";

    const maisRecente = importData.reduce((maisNovo, atual) =>
      atual.created > maisNovo.created ? atual : maisNovo
    );

    return formatarData(maisRecente.created);
  }, [importData]);

  function formatarData(timestamp: number) {
    const data = new Date(timestamp);

    const dia = String(data.getDate()).padStart(2, "0");
    const mes = String(data.getMonth() + 1).padStart(2, "0");
    const ano = data.getFullYear();

    const horas = String(data.getHours()).padStart(2, "0");
    const minutos = String(data.getMinutes()).padStart(2, "0");

    return `${dia}/${mes}/${ano} às ${horas}:${minutos}`;
  }
  // Carregar logo ao montar componente
  useEffect(() => {
    loadLogo();
  }, []);

  // Carregar rankings quando a aba de rankings for ativada
  useEffect(() => {
    if (activeTab === "rankings") {
      loadRankings();
    }
  }, [activeTab]);

  // Função para carregar logo
  const loadLogo = async () => {
    try {
      setLoadingLogo(true);
      const response = await getGlobalLogo();
      if (response && response.logoUrl) {
        setLogoUrl(response.logoUrl);
        setLogoData(response);
      } else {
        console.log("Nenhuma logo encontrada na resposta");
      }
    } catch (error) {
      console.error("Erro ao carregar logo:", error);
    } finally {
      setLoadingLogo(false);
    }
  };

  // Função para fazer upload da logo
  const handleLogoUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar tipo de arquivo
    if (!file.type.startsWith("image/")) {
      alert("Por favor, selecione uma imagem válida.");
      return;
    }

    try {
      setUploadingLogo(true);

      // Aqui você deve fazer upload da imagem para o S3 ou servidor
      // Por enquanto, vou simular com um URL local (você precisa implementar o upload real)
      const reader = new FileReader();
      reader.onloadend = async () => {
        const newLogoUrl = reader.result as string;

        // Preparar dados para salvar
        const logoPayload = {
          _id: "global_logo",
          logoUrl: newLogoUrl, // Em produção, use a URL do S3 após upload
          created: logoData?.created || Date.now(),
          updated: { $date: Date.now() },
        };

        try {
          await updateGlobalLogo(logoPayload);
          setLogoUrl(newLogoUrl);
          setLogoData(logoPayload);
          alert("Logo atualizada com sucesso!");
        } catch (error) {
          console.error("Erro ao salvar logo:", error);
          alert("Erro ao salvar logo. Por favor, tente novamente.");
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error("Erro no upload da logo:", error);
      alert("Erro no upload da logo. Por favor, tente novamente.");
    } finally {
      setUploadingLogo(false);
    }
  };

  // Função para carregar rankings
  const loadRankings = async () => {
    try {
      setLoadingRankings(true);
      const rankings = await getAllRankings();
      setRankingsList(rankings);
    } catch (error) {
      console.error("Erro ao carregar rankings:", error);
      alert("Erro ao carregar rankings. Por favor, tente novamente.");
    } finally {
      setLoadingRankings(false);
    }
  };

  // Função para deletar ranking
  const handleDeleteRanking = async (
    rankingId: string,
    rankingName: string
  ) => {
    const confirmed = window.confirm(
      `Tem certeza que deseja excluir o ranking "${rankingName}"? Esta ação não pode ser desfeita.`
    );

    if (!confirmed) return;

    try {
      await deleteRanking(rankingId);
      alert("Ranking excluído com sucesso!");
      loadRankings(); // Recarregar a lista
    } catch (error) {
      console.error("Erro ao deletar ranking:", error);
      alert("Erro ao excluir ranking. Por favor, tente novamente.");
    }
  };

  const { listPlayer } = useListPlayer();
  const [players, setPlayers] = useState<any[]>([]);

  useEffect(() => {
    const fetchPlayers = async () => {
      try {
        const data = await listPlayer();
        console.log('Jogadores carregados da API:', data);
        setPlayers(data);
      } catch (error) {
        console.error("Erro ao listar jogadores:", error);
      }
    };

    fetchPlayers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Log quando o estado players for atualizado
  useEffect(() => {
    if (players.length > 0) {
      console.log('Estado players atualizado:', players);
    }
  }, [players]);

  const handleCreateRanking = () => {
    navigate("/gestao/ranking/novo");
  };

  const handleEditRanking = (rankingId: string) => {
    navigate(`/gestao/ranking/editar/${rankingId}`);
  };

  // Função para importar do Google Sheets
  const handleGoogleSheetsImport = async () => {
    if (!googleSheetsUrl.trim()) {
      alert("Por favor, insira a URL do Google Sheets");
      return;
    }

    try {
      setImportingFromSheets(true);

      const result = await importRankingFromGoogleSheets(googleSheetsUrl);

      if (result.success) {
        // Adicionar ao
        const newImport: ImportHistory = {
          date: new Date().toLocaleString("pt-BR"),
          total: result.total,
          success: result.total,
          errors: 0,
          status: "success",
        };

        setImportHistory([newImport, ...importHistory]);

        setGoogleSheetsUrl("");

        alert(result.message);
      } else {
        alert(`Erro na importação: ${result.message}`);
      }
    } catch (error) {
      console.error("Erro na importação do Google Sheets:", error);
      alert(
        "Erro inesperado durante a importação. Verifique o console para mais detalhes."
      );
    } finally {
      setImportingFromSheets(false);
    }
  };

  const handleDownloadTemplate = () => {
    // Criar template de planilha
    const templateData = [
      ["email", "nome", "pontos"],
      ["usuario1@email.com", "João Silva", "100"],
      ["usuario2@email.com", "Maria Santos", "150"],
    ];

    // Converter para CSV
    const csvContent = templateData.map((row) => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", "modelo_importacao_rankifier.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImportSuccess = (result: ImportResult) => {
    setIsImportModalOpen(false);

    console.log("Importação realizada com sucesso!", {
      total: result.total,
      sucesso: result.success,
      erros: result.errors,
    });
  };

  const handleDownloadParticipantsTemplate = () => {
    // Criar CSV com colunas: nome, email
    const csvContent = 'nome,email\nJoão Silva,joao@example.com\nMaria Santos,maria@example.com\nPedro Costa,pedro@example.com';
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', 'modelo_participantes_rankifier.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };


  return (
    <>
      <Helmet>
        <title>Gestão - Rankifier</title>
      </Helmet>

      <div className="gestao-page">
        <HeaderGestor />

        <div className="gestao-container">
          <div className="gestao-content">
            <div className="gestao-card">
              <div className="gestao-header">
                <h1 className="gestao-title">Rankifier Gestão</h1>
              </div>

              <div className="gestao-layout">
                {/* Sidebar Navigation */}
                <div className="gestao-sidebar">
                  <nav className="gestao-nav">
                    {tabs.map((tab) => {
                      const Icon = tab.icon;
                      return (
                        <button
                          key={tab.id}
                          onClick={() => setActiveTab(tab.id)}
                          className={`nav-button ${
                            activeTab === tab.id ? "active" : ""
                          }`}
                        >
                          <Icon size={20} className="nav-icon" />
                          <span>{tab.label}</span>
                        </button>
                      );
                    })}
                  </nav>
                </div>

                {/* Main Content */}
                <div className="gestao-main">
                  {activeTab === "importar" && (
                    <div className="tab-content">
                      <h2 className="tab-title">Importar dados</h2>
                      <p className="tab-description">
                        Gerencie importações de dados para garantir a pontuação
                        dos participantes
                      </p>

                      <div className="import-section">
                        <div className="import-card">
                          <div className="import-header">
                            <div className="import-icon-container">
                              <CloudUpload
                                size={40}
                                className="import-main-icon"
                              />
                            </div>
                            <h3 className="import-card-title">
                              Importação de Dados
                            </h3>
                          </div>

                          <div className="import-status-section">
                            <div className="import-status-header">
                              <span className="import-status-label">
                                Última Importação
                              </span>
                              <span className="import-status-date">
                                {dataMaisRecenteFormatada}
                              </span>
                            </div>

                            <div className="import-stats-row">
                              <div className="import-stat-item">
                                <span className="import-stat-label">
                                  Total de registros:
                                </span>
                                <span className="import-stat-value">{dataTotal}</span>
                              </div>
                              <div className="import-stat-item">
                                <span className="import-stat-label">
                                  Processados com sucesso:
                                </span>
                                <span className="import-stat-value success">
                                  {totalCompleted}
                                </span>
                              </div>
                              <div className="import-stat-item">
                                <span className="import-stat-label">
                                  Erros:
                                </span>
                                <span className="import-stat-value error">
                                  0 {/* tratar erro depois */}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="import-actions">
                            <button
                              className="btn-import-primary"
                              onClick={() => setIsImportModalOpen(true)}
                            >
                              <CloudUpload size={20} />
                              Nova Importação
                            </button>
                          </div>
                        </div>

                        {/* Seção do Google Sheets 
                        <div className="import-card">
                          <div className="import-header">
                            <div className="import-icon-container">
                              <FileText
                                size={40}
                                className="import-main-icon"
                              />
                            </div>
                            <h3 className="import-card-title">
                              Importação via Google Sheets
                            </h3>
                          </div>

                          <div className="google-sheets-section">
                            <p className="google-sheets-description">
                              Cole a URL da sua planilha do Google Sheets para
                              importar os dados diretamente.
                            </p>

                            <div className="google-sheets-input-group">
                              <label
                                htmlFor="googleSheetsUrl"
                                className="google-sheets-label"
                              >
                                URL do Google Sheets
                              </label>
                              <input
                                id="googleSheetsUrl"
                                type="url"
                                className="google-sheets-input"
                                placeholder="https://docs.google.com/spreadsheets/d/..."
                                value={googleSheetsUrl}
                                onChange={(e) =>
                                  setGoogleSheetsUrl(e.target.value)
                                }
                                disabled={importingFromSheets}
                              />
                            </div>

                            <div className="google-sheets-requirements">
                              <h5>Formato obrigatório da planilha:</h5>
                              <ul>
                                <li>
                                  <strong>1ª coluna:</strong> Nomes dos usuários
                                </li>
                                <li>
                                  <strong>2ª coluna:</strong> Pontos (valores
                                  numéricos)
                                </li>
                                <li>
                                  Certifique-se de que a planilha esteja pública
                                  ou compartilhada
                                </li>
                              </ul>
                            </div>

                            <button
                              className="btn-import-sheets"
                              onClick={handleGoogleSheetsImport}
                              disabled={
                                !googleSheetsUrl.trim() || importingFromSheets
                              }
                            >
                              {importingFromSheets ? (
                                <>
                                  <div className="loading-spinner"></div>
                                  Importando...
                                </>
                              ) : (
                                <>
                                  <FileText size={20} />
                                  Importar do Google Sheets
                                </>
                              )}
                            </button>
                          </div>
                        </div> 

                        <div className="import-info-card">
                          <div className="info-header">
                            <FileText size={20} className="info-icon" />
                            <h4>Como importar dados</h4>
                          </div>
                          <ol className="info-steps-list">
                            <li>
                              Baixe o modelo de planilha clicando no botão acima
                            </li>
                            <li>
                              Preencha os dados dos participantes seguindo o
                              formato
                            </li>
                            <li>Salve o arquivo no formato .xlsx ou .csv</li>
                            <li>
                              Clique em "Nova Importação" e selecione o arquivo
                            </li>
                            <li>
                              Aguarde o processamento e verifique o relatório
                            </li>
                          </ol>
                        </div> */}
                      </div>
                    </div>
                  )} 

                  {activeTab === "rankings" && (
                    <div className="tab-content">
                      <h2 className="tab-title">Configuração de Rankings</h2>
                      <p className="tab-description">
                        Configure métricas, períodos e filtros para seus
                        rankings
                      </p>

                      <div className="rankings-section">
                        <div className="rankings-header">
                          <button
                            className="btn-create-ranking"
                            onClick={handleCreateRanking}
                          >
                            <UserPlus size={18} />
                            Criar novo ranking
                          </button>
                        </div>

                        <div className="rankings-list">
                          {loadingRankings ? (
                            <div className="rankings-loading">
                              <div className="spinner"></div>
                              <p>Carregando rankings...</p>
                            </div>
                          ) : rankingsList.length > 0 ? (
                            rankingsList.map((ranking) => (
                              <div key={ranking._id} className="ranking-item">
                                <div className="ranking-item-content">
                                  <div className="ranking-item-icon">
                                    <BarChart3 size={18} />
                                  </div>
                                  <div className="ranking-item-details">
                                    <span className="ranking-item-name">
                                      {ranking.title}
                                    </span>
                                    <span className="ranking-item-meta">
                                      {ranking.period === "semanal" &&
                                        "Semanal"}
                                      {ranking.period === "mensal" && "Mensal"}
                                      {ranking.period === "anual" && "Anual"}
                                      {" • "}
                                      {ranking.participants.length}{" "}
                                      participante(s)
                                      {" • "}
                                      Layouts:{" "}
                                      {ranking.visuals
                                        .map((v) => {
                                          if (v === "podio") return "Pódio";
                                          if (v === "lista") return "Lista";
                                          if (v === "grid") return "Grid";
                                          if (v === "privado") return "Privado";
                                          return v;
                                        })
                                        .join(", ")}
                                    </span>
                                  </div>
                                </div>
                                <div className="ranking-item-actions">
                                  <button
                                    className="ranking-item-edit"
                                    title="Editar ranking"
                                    onClick={() =>
                                      ranking._id &&
                                      handleEditRanking(ranking._id)
                                    }
                                  >
                                    <Edit size={16} />
                                  </button>
                                  <button
                                    className="ranking-item-delete"
                                    title="Excluir ranking"
                                    onClick={() =>
                                      ranking._id &&
                                      handleDeleteRanking(
                                        ranking._id,
                                        ranking.title
                                      )
                                    }
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="rankings-empty">
                              <BarChart3 size={48} />
                              <p>Nenhum ranking criado ainda</p>
                              <button
                                className="btn-create-ranking-empty"
                                onClick={handleCreateRanking}
                              >
                                <UserPlus size={18} />
                                Criar primeiro ranking
                              </button>
                            </div>
                          )}
                        </div>

                        <div className="rankings-info-card">
                          <div className="info-icon-wrapper">
                            <BarChart3 size={24} className="info-icon-large" />
                          </div>
                          <div className="info-content">
                            <h4 className="info-title">
                              Gerencie seus rankings
                            </h4>
                            <p className="info-text">
                              Crie novos rankings ou edite os existentes para
                              personalizar métricas, períodos de avaliação e
                              critérios de pontuação para seus participantes.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === "participantes" && (
                    <div className="tab-content">
                      <h2 className="tab-title">Gerenciamento de Participantes</h2>
                      <p className="tab-description">Altere permissões entre gestor e jogador e envie convites</p>



                      <div className="participants-section">
                        <div className="section-header-participants">
                          <button 
                            className="btn-add-participant"
                            onClick={() => setIsImportParticipantsModalOpen(true)}
                          >
                            <UserPlus size={16} />
                            Importar Planilha com participantes
                          </button>
                          <button 
                            className="btn-add-participant"
                            onClick={() => setIsManualAddModalOpen(true)}
                          >
                            <UserPlus size={16} />
                            Adicionar participante manualmente
                          </button>
                        </div>

                        <div className="participants-list pt-10">
                          <div className="participants-title-group">
                            <Users size={20} className="section-icon text-white " />
                            <h3 className='text-white'>Lista de Participantes</h3>
                          </div>
                          {players.map(player => (
                            <div key={player?._id || player?.id} className="participant-card">

                              <div className="participant-info">
                                <div className="participant-avatar-circle">
                                  {player?.image?.small?.url ? (
                                    <img 
                                      src={player.image.small.url} 
                                      alt={player.name} 
                                      className="participant-avatar-img"
                                    />
                                  ) : player?.foto_de_perfil ? (
                                    <img 
                                      src={player.foto_de_perfil} 
                                      alt={player.name} 
                                      className="participant-avatar-img"
                                    />
                                  ) : (
                                    <div className="participant-avatar-placeholder">
                                      {player?.name?.charAt(0)?.toUpperCase() || '?'}
                                    </div>
                                  )}
                                </div>
                                <div className="participant-details">
                                  <div className="participant-name-text">
                                    {player?.name}
                                  </div>
                                  <div className="participant-email">
                                    {player?.email}
                                  </div>
                                </div>
                              </div>
                              <div className="participant-controls">
                                {(player?.role === 'gestor' || player?.extra?.role === 'gestor') && (
                                  <div className="participant-gestor-badge">Gestor</div>
                                )}        
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Seção de Campos de Participantes 

                        <div className="participant-fields-section">
                          <h4 className="fields-section-title">
                            Participantes:
                          </h4>
                          <div className="participant-input-row">
                            <div className="input-group-participant">
                              <label className="input-label-participant">
                                E-mail
                              </label>
                              <input
                                type="email"
                                className="input-field-participant"
                                placeholder="email@exemplo.com"
                              />
                            </div>
                            <div className="input-group-participant">
                              <label className="input-label-participant">
                                Nome
                              </label>
                              <input
                                type="text"
                                className="input-field-participant"
                                placeholder="Nome do participante"
                              />
                            </div>
                          </div>
                        </div> */}

                        <div className="permissions-info-section">
                          <h4 className="permissions-title">
                            Permissões por Função
                          </h4>
                          <div className="permissions-comparison">
                            <div className="permission-column">
                              <h5 className="permission-role-title">Gestor</h5>
                              <ul className="permission-list">
                                <li>Visualizar todos os rankings</li>
                                <li>Configurar métricas e períodos</li>
                                <li>Gerenciar participantes</li>
                                <li>Acessar relatórios completos</li>
                                <li>Enviar convites</li>
                              </ul>
                            </div>
                            <div className="permission-column">
                              <h5 className="permission-role-title">Jogador</h5>
                              <ul className="permission-list">
                                <li>Visualizar próprio desempenho</li>
                                <li>Ver ranking geral</li>
                                <li>Receber notificações pessoais</li>
                                <li>Visualizar perfil</li>
                                <li>Atualizar perfil</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === "configuracoes" && (
                    <div className="tab-content">
                      <h2 className="tab-title">Configurações</h2>
                      <p className="tab-description">
                        Personalize a logo, cores e configure integrações
                      </p>

                      <div className="config-sections">
                        {/* Logo da Empresa */}
                        <div className="config-section-card">
                          <div className="config-card-header">
                            <Palette size={20} className="config-icon" />
                            <h3>Logo da Empresa</h3>
                          </div>
                          <div className="logo-upload-section">
                            <div className="logo-preview-area">
                              {loadingLogo ? (
                                <div className="logo-placeholder-circle">
                                  <div className="spinner"></div>
                                </div>
                              ) : logoUrl ? (
                                <img
                                  src={logoUrl}
                                  alt="Logo da empresa"
                                  className="logo-preview-image"
                                  style={{
                                    maxWidth: "200px",
                                    maxHeight: "200px",
                                    objectFit: "contain",
                                  }}
                                />
                              ) : (
                                <div className="logo-placeholder-circle">
                                  LOGO
                                </div>
                              )}
                              <p className="logo-status-text">
                                {logoUrl
                                  ? "Logo atual da empresa"
                                  : "Nenhuma logo configurada"}
                              </p>
                            </div>
                            <input
                              type="file"
                              id="logo-upload-input"
                              accept="image/*"
                              style={{ display: "none" }}
                              onChange={handleLogoUpload}
                              disabled={uploadingLogo}
                            />
                            <button
                              className="btn-upload-logo"
                              onClick={() =>
                                document
                                  .getElementById("logo-upload-input")
                                  ?.click()
                              }
                              disabled={uploadingLogo}
                            >
                              <Upload size={16} />
                              {uploadingLogo ? "Salvando..." : "Alterar Logo"}
                            </button>
                          </div>
                        </div>

                        {/* Personalização de Cores */}
                        {/*}
                        <div className="config-section-card">
                          <div className="config-card-header">
                            <Palette size={20} className="config-icon" />
                            <h3>Personalização de Cores</h3>
                          </div>
                          <p className="config-card-subtitle">
                            Defina as cores da sua marca
                          </p>

                          <div className="colors-config-grid">
                            <div className="color-config-group">
                              <label className="color-label">
                                Cor Primária
                              </label>
                              <div className="color-input-group">
                                <div
                                  className="color-preview"
                                  style={{ background: "#8B5CF6" }}
                                ></div>
                                <input
                                  type="text"
                                  className="color-text-input"
                                  defaultValue="#8B5CF6"
                                  placeholder="#8B5CF6"
                                />
                              </div>
                            </div>
                            <div className="color-config-group">
                              <label className="color-label">
                                Cor Secundária
                              </label>
                              <div className="color-input-group">
                                <div
                                  className="color-preview"
                                  style={{ background: "#374151" }}
                                ></div>
                                <input
                                  type="text"
                                  className="color-text-input"
                                  defaultValue="#374151"
                                  placeholder="#374151"
                                />
                              </div>
                            </div>
                          </div>

                          <button className="btn-apply-colors">
                            Aplicar Cores
                          </button>
                        </div> */}

                        {/* Integrações */}
                        {/*}
                        <div className="config-section-card">
                          <div className="config-card-header">
                            <Sparkles size={20} className="config-icon" />
                            <h3>Integrações</h3>
                          </div>
                          <p className="config-card-subtitle">
                            Conecte o sistema com outras ferramentas
                          </p>

                          <div className="integrations-grid">
                            <div className="integration-card">
                              <div className="integration-content">
                                <strong className="integration-name">
                                  Slack
                                </strong>
                                <p className="integration-description">
                                  Notificações em tempo real
                                </p>
                              </div>
                              <label className="toggle-switch">
                                <input type="checkbox" />
                                <span className="toggle-slider"></span>
                              </label>
                            </div>
                            <div className="integration-card">
                              <div className="integration-content">
                                <strong className="integration-name">
                                  Google Sheets
                                </strong>
                                <p className="integration-description">
                                  Sincronização de dados
                                </p>
                              </div>
                              <label className="toggle-switch">
                                <input type="checkbox" />
                                <span className="toggle-slider"></span>
                              </label>
                            </div>
                            <div className="integration-card">
                              <div className="integration-content">
                                <strong className="integration-name">
                                  API
                                </strong>
                                <p className="integration-description">
                                  Integração via API
                                </p>
                              </div>
                              <label className="toggle-switch">
                                <input type="checkbox" defaultChecked />
                                <span className="toggle-slider"></span>
                              </label>
                            </div>
                          </div>
                        </div> */}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Importação de Dados */}
      <ImportDataModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onImportSuccess={handleImportSuccess}
      />

      {/* Modal de Importação de Participantes */}
      <ImportParticipantsModal
        isOpen={isImportParticipantsModalOpen}
        onClose={() => setIsImportParticipantsModalOpen(false)}
        onDownloadTemplate={handleDownloadParticipantsTemplate}
        onSuccess={() => {
          setIsImportParticipantsModalOpen(false);
          // Aqui você pode adicionar lógica para atualizar a lista de participantes
        }}
      />

      {/* Modal de Adicionar Participante Manualmente */}
      <ImportParticipantsModal
        isOpen={isManualAddModalOpen}
        onClose={() => setIsManualAddModalOpen(false)}
        onSuccess={() => {
          setIsManualAddModalOpen(false);
          // Aqui você pode adicionar lógica para atualizar a lista de participantes
        }}
        isManualMode={true}
      />
    </>
  );
};

export default Gestao;
